import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AnniversaryData } from '../types/types';
import initialData from '../utils/data';

interface AnniversaryContextType {
  data: AnniversaryData;
  updateMetric: (id: string, newValue: string | number) => void;
  updatePhoto: (id: string, photoData: Partial<AnniversaryData['photos'][0]>) => void;
  updatePlaylist: (playlistData: Partial<AnniversaryData['playlist']>) => void;
  isLoading: boolean;
}

const AnniversaryContext = createContext<AnniversaryContextType | undefined>(undefined);

interface AnniversaryProviderProps {
  children: ReactNode;
}

export const AnniversaryProvider: React.FC<AnniversaryProviderProps> = ({ children }) => {
  const [data, setData] = useState<AnniversaryData>(initialData);
  const isLoading = false;

  // Update a specific metric value
  const updateMetric = (id: string, newValue: string | number) => {
    setData(prevData => ({
      ...prevData,
      metrics: prevData.metrics.map(metric => 
        metric.id === id ? { ...metric, value: newValue } : metric
      )
    }));
  };

  // Update a photo's data
  const updatePhoto = (id: string, photoData: Partial<AnniversaryData['photos'][0]>) => {
    setData(prevData => ({
      ...prevData,
      photos: prevData.photos.map(photo => 
        photo.id === id ? { ...photo, ...photoData } : photo
      )
    }));
  };

  // Update playlist data
  const updatePlaylist = (playlistData: Partial<AnniversaryData['playlist']>) => {
    setData(prevData => ({
      ...prevData,
      playlist: { ...prevData.playlist, ...playlistData }
    }));
  };

  const value = {
    data,
    updateMetric,
    updatePhoto,
    updatePlaylist,
    isLoading
  };

  return (
    <AnniversaryContext.Provider value={value}>
      {children}
    </AnniversaryContext.Provider>
  );
};

// Custom hook to use the Anniversary context
export const useAnniversary = () => {
  const context = useContext(AnniversaryContext);
  if (context === undefined) {
    throw new Error('useAnniversary must be used within an AnniversaryProvider');
  }
  return context;
}; 